import React, { useState, useEffect } from "react";
import { Routes, Route, Switch, withRouter } from "react-router-dom";
import request from "request"
import Terminal from "lib/auth/Terminal"

import Form from "Form"
import Input from "Input"

import ItemsList from "./Register/ItemsList"
import ProductView from "./Register/ProductView"
import OrderView from "./Register/OrderView"

/* Modal imports start here */
import PaymentView from "./Register/PaymentView"
import AddCreditModal from "./Register/AddCreditModal"
import CustomerView from "./Register/CustomerView"
import MergeOrderView from "./Register/MergeOrderView"
/* Modal imports end here */

import Flash from "flash"
import { set } from "lodash";

const MERGE_ORDER = "merge_order";
const CLEAR_ORDER = "clear_order";

const Register = () => {
  const [ selectedProduct, setSelectedProduct ] = useState(null)
  const [ order, setOrder ] = useState(null)
  const [ orderId, setOrderId ] = useState(Terminal.getCurrentOrderId())
  // productCount is a cache counter for the Cart aka OrderView
  const [ productCount, setProductCount ] = useState(0)

  // tabCount is not the number of tabs, it is a cache counter for Tabs
  // every time we need to reload the tabs(new tab added or a product added to an existing tab)
  // we will increase the counter and let ItemList rerender OrderListView( which contains tabs)
  // a bit unusual, unortodox React approach that I chose to use instead of refactoring Register to ensure that
  // - Discount
  // - Order
  // - New Customer
  // - and Tabs components are properly connected

  // I wasn't hired to improve code modularity, let's focus on the task at hand. And to refactor something I need good UI tests.
  // I didn't make the code worse, the new components are well separated but the connection between them is not D(IP) as in SOLID
  const [ tabCount, setTabCount] = useState(0)
  const [selectedTab, setSelectedTab] = useState(null)

  const [ isModifyingOrder, setIsModifyingOrder ] = useState(false)

  /* Modal visibility controls start here */
  const [ isPaymentActive, setIsPaymentActive ] = useState(false)
  const [ isCreditModalOpen, setIsCreditModalOpen ] = useState(false)
  const [ isCustomerActive, setIsCustomerActive ] = useState(false)
  const [ isMergeOrderActive, setIsMergeOrderActive ] = useState(false)
  /* Modal visibility controls end here */

  const [ currentView, setCurrentView ] = useState(Terminal.getLayout() || "list")
  const { terminal } = Terminal.getWhoAmI()
  const clearProduct = () => setSelectedProduct(null)

  const clearOrder = () => {
    setOrder(null)
    setOrderId(null)
    setProductCount(0)
    Terminal.clearCurrentOrderId()
  }

  const orderComplete = () => {
    if(order?.isOnTab) {
      updateTab()
    }
    clearOrder()
  }

  const fetchOrder = () => {
    if (orderId == "null" || orderId === null) return false

    const req = request.get(`carts/${orderId}`)
    req.then(({data}) => {
      if(data.error) {
        clearOrder()
      } else {
        setOrder(data.order)
        setProductCount(data.order.products.reduce((acc, p) => acc + p.quantity, 0))
        setIsModifyingOrder(false)

        if(data.order?.isOnTab) {
          updateTab()
        }
      }
    })
  }

  useEffect(fetchOrder, [ productCount ])

  const createOrder = async () => {
    if(order) return false

    const orderAttrs = {
      type: "terminal",
      storeId: terminal.store.id,
      terminalId: terminal.id,
    }

    const req = await request.post("carts", orderAttrs)
    return req.data.order.id
  }

  const addProduct = async (productId, quantity, selectedModifiers = [], customPrice = null, customNotes = null) => {
    let newOrderId = null
    setIsModifyingOrder(true)

    if(!orderId) {
      newOrderId = await createOrder()

      if (newOrderId) {
        setOrderId(newOrderId)
        Terminal.setCurrentOrderId(newOrderId)
        setProductCount(0)
      }
    }

    const inflightOrderId = newOrderId || orderId

    if (inflightOrderId === "null" || inflightOrderId == null) return setIsModifyingOrder(false)

    const req = request.post(`/carts/${inflightOrderId}/line_items`, {
      productId: productId,
      amount: customPrice,
      notes: customNotes,
      line_item: {
        quantity,
        selectedModifiers
      }
    })
    req.then(({data}) => {
      setProductCount(productCount + quantity)
    })
  }

  const updateTab = () => {
    setTabCount(tabCount + 1)
  }

  const quickAddDiscount = async (discountId) => {
    let newOrderId = null
    setIsModifyingOrder(true)
    if(!orderId) {
      newOrderId = await createOrder()

      if (newOrderId) {
        setOrderId(newOrderId)
        Terminal.setCurrentOrderId(newOrderId)
        setProductCount(0)
      }
    }

    const inflightOrderId = newOrderId || orderId

    if (inflightOrderId === "null" || inflightOrderId == null) return setIsModifyingOrder(false)
    const req = request.post(`/carts/${inflightOrderId}/discounts`, {
      discount_id: discountId
      }
    )
    req.then(({data}) => {
      setProductCount(productCount + 1)
    }).catch((error) => {
      if (error.response && error.response.data && error.response.data.errors) {
        Flash.broadcast("error", error.response.data.errors.join(", "))
      } else {
        Flash.broadcast("error", "There was a problem applying the discount")
      }
    }).finally(() => {
      setIsModifyingOrder(false)
    })
  }
  const removeDiscount = (orderId, discountId) => {
    setIsModifyingOrder(true)

    const req = request.delete(`/carts/${orderId}/discounts/${discountId}`)
    req.then(({data}) => {
      setProductCount(productCount - 1)
    })
  }

  const quickAddGratuity = async (gratuityId) => {
    let newOrderId = null
    setIsModifyingOrder(true)
    if(!orderId) {
      newOrderId = await createOrder()

      if (newOrderId) {
        setOrderId(newOrderId)
        Terminal.setCurrentOrderId(newOrderId)
        setProductCount(0)
      }
    }

    const inflightOrderId = newOrderId || orderId

    if (inflightOrderId === "null" || inflightOrderId == null) return setIsModifyingOrder(false)
    const req = request.post(`/carts/${inflightOrderId}/gratuities`, {
      gratuity_id: gratuityId
    })
    req.then(({data}) => {
      setProductCount(productCount + 1)
    }).catch((error) => {
      if (error.response && error.response.data && error.response.data.errors) {
        Flash.broadcast("error", error.response.data.errors.join(", "))
      } else {
        Flash.broadcast("error", "There was a problem applying the gratuity")
      }
    }).finally(() => {
      setIsModifyingOrder(false)
    })
  }

  const removeGratuity = (orderId, gratuityId) => {
    setIsModifyingOrder(true)

    const req = request.delete(`/carts/${orderId}/gratuities/${gratuityId}`)
    req.then(({data}) => {
      setProductCount(productCount - 1)
    })
  }

  const removeProduct = (lineItemId, quantity) => {
    setIsModifyingOrder(true)

    const req = request.delete(`/carts/${orderId}/line_items/${lineItemId}`)
    req.then(({data}) => {
      setProductCount(productCount - quantity)
    })
  }
  const switchViewFor = (view) => {
    return () => {
      if (view == "product") {
        const newView = currentView === "list" ? "grid" : "list";
        setCurrentView(newView);
        Terminal.setLayout(newView);
      }

      if (view == "categories") {
        setCurrentView("categories");
        Terminal.setLayout("categories");
      }

      if (view == "discount-list") {
        setCurrentView("discount-list");
        Terminal.setLayout("discount-list");
      }

      if (view == "tabs-list") {
        setCurrentView("tabs-list");
        Terminal.setLayout("tabs-list");
      }

      if (view == "refunds-list") {
        setCurrentView("refunds-list");
        Terminal.setLayout("refunds-list");
      }
    };
  };

  const putOnExistingTab = () => {
    Terminal.setLayout("tabs-list");
    setCurrentView("tabs-list");

  }
  const putOnNewTab = () => {
    Terminal.setLayout("tabs-list");
    setCurrentView("tabs-list");
    setIsCustomerActive(true);
  }

  const removeCredits = (prepaidCreditId = null) => {
    if(!order.hasPrepaidCredit) return false
    setIsModifyingOrder(true)

    // If prepaidCreditId is provided, remove only that credit; otherwise remove all
    const url = prepaidCreditId
      ? `/carts/${order.id}/prepaid_credits/?prepaid_credit_id=${prepaidCreditId}`
      : `/carts/${order.id}/prepaid_credits/`;

    const req = request.delete(url)

    req.then(({data}) => {
      setIsModifyingOrder(false)
      setOrder(data.cart)
    })
    req.catch(() => {
      Flash.broadcast("error", "There was a problem removing the credits")
    })
    req.finally(() => setIsModifyingOrder(false))
  }

  // Set (order on) Tab as the current Order
  const setTab= (tabId) => {
    clearOrder();
    setOrderId(tabId);
    // The following line is a trade off
    // here we use productCount change to trigger fetchOrder via useEffect
    // the code before Tabs had only one order and the only reason to fetch it was the productCount change
    // now we can have multiple orders, and now it makes sense to useEffect on orderId change to fetchOrder
    // However, I belive simpler code is better
    // less code is better
    // Also my goal is to reuse as much code as possible
    // setProductCount(-1) will simply trigger fetchOrder and after the order is fetched the product count will be set to a new value
    setProductCount(-1);
  }

  // Select Tab
  // If there is no order in the card, or the order in the card is also on Tab
  // just set the selected tab as the current order
  // else
  // ask the user to either discard the current order or add the current order to the selected tab
  //
  // The logic is a bit complex but it allows us to create a simple, straightforward UI
  // A UI where the user don't need to think about the current state, the user just take actions and respond to the dialogs
  // A UI where there is just one button "Select Tab", that does 3 things depending on the current state
  // The user don't need to think which button to click, just hit "Select Tab" and you will eventually have what you need
  const selectTab = (tabOrder, action = null) => {
    if(order && !order.isOnTab) {
      switch(action) {
        case MERGE_ORDER:
          mergeOrder(tabOrder);
          setTab(tabOrder.id);
          break;
        case CLEAR_ORDER:
          setTab(tabOrder.id);
          break;
        case null:
          // selectedTab used only in MergeOrderView
          // it doesn't mean it is selected for the OrderView
          // multi-component React :idk:
          setSelectedTab(tabOrder);

          setIsMergeOrderActive(true);
          // there is an order in the cart and there is no action, we don't know what to do
          // just show the merge order modal and return
          // MergeOrderView will call this method back with an action, either merge_order or clear_order
          break;
      }
    } else {
      setTab(tabOrder.id);
    }
  }

  // Merge current order into the tab
  const mergeOrder = async (tabOrder) => {
    const req = await request.post(`/orders/${tabOrder.id}/merge`, { cart_order_id: order.id })
    return req.data
  }

  const mergeAndSelect = (tab) => { selectTab(tab, MERGE_ORDER); }
  const clearAndSelect = (tab) => { selectTab(tab, CLEAR_ORDER); }

  return(
    <div className="columns">
      <div className="column" style={{maxHeight: "100%", overflowY: "scroll", overflow: "hidden"}}>
        <ItemsList
          terminal={terminal}
          store={terminal.store}
          orderId={orderId}
          currentView = {currentView}
          switchViewFor={switchViewFor}
          selectProduct={setSelectedProduct}
          addProduct={addProduct}
          quickAddDiscount={quickAddDiscount}
          quickAddGratuity={quickAddGratuity}
          selectTab={selectTab}
          tabCount={tabCount}
          isModifyingOrder={isModifyingOrder}
        />
      </div>
      <div className="column is-one-third mt-4">
        <OrderView
          order={order}
          terminal={terminal}
          store={terminal.store}
          clearOrder={clearOrder}
          fetchOrder={fetchOrder}
          requestCredit={() => setIsCreditModalOpen(true)}
          removeCredits={removeCredits}
          removeProduct={removeProduct}
          productCount={productCount}
          removeDiscount={removeDiscount}
          removeGratuity={removeGratuity}
          putOnExistingTab={putOnExistingTab}
          putOnNewTab={putOnNewTab}
          updateTab={updateTab}
          isModifyingOrder={isModifyingOrder}
          setIsPaymentActive={setIsPaymentActive}
          chargesCustomerFees={terminal.chargesCustomerFees}
          hasPrepaidEnabled={terminal.hasPrepaidEnabled}
          allowTaxToggle={terminal.allowTaxToggle}
        />
      </div>

      <ProductView
        clearProduct={clearProduct}
        selectedProduct={selectedProduct}
        isModifyingOrder={isModifyingOrder}
        addProduct={addProduct}
      />

      <PaymentView
        order={order}
        terminal={terminal}
        isPaymentActive={isPaymentActive}
        setIsPaymentActive={setIsPaymentActive}
        orderComplete={orderComplete}
      />

      <AddCreditModal
        order={order}
        isOpen={isCreditModalOpen}
        onClose={() => setIsCreditModalOpen(false)}
        refreshOrder={fetchOrder}
      />

      <CustomerView
        order={order}
        clearOrder={clearOrder}
        isCustomerActive={isCustomerActive}
        updateTab={updateTab}
        setIsCustomerActive={setIsCustomerActive}
      ></CustomerView>

      {selectedTab && <MergeOrderView
        selectedTab={selectedTab}
        mergeAndSelect={mergeAndSelect}
        clearAndSelect={clearAndSelect}
        isMergeOrderActive={isMergeOrderActive}
        setIsMergeOrderActive={setIsMergeOrderActive}
      />}
    </div>
  )
}

export default Register
